// ShareSuccessModal — виральный момент сразу после aha (§ GROWTH-OS-v9 петли 3/4).
//
// Показывается в пик-момент (peak-end rule, §10a): первый пост запланирован.
// Даёт (а) шеринг результата с брендом teleclaw (артефактная вирусность, петля 3),
// (б) реф-ссылку give-to-get (петля 4). Инвайт предлагается ИМЕННО в момент aha
// (↓ виральный цикл, §7a). Никаких dark patterns — закрыть можно в 1 клик.
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Share2, Copy, X, PartyPopper } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { track } from '../utils/track';

interface ReferralInfo {
  link: string;
  invitedCount?: number;
  earned?: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  surface: string;
  channelHandle?: string;
  subscribers?: number;
}

export function ShareSuccessModal({ open, onClose, surface, channelHandle, subscribers }: Props): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Реф-ссылка: пытаемся взять каноничную с бэкенда, иначе строим из origin + ?ref=uid.
  const { data: referral } = useQuery<ReferralInfo>({
    queryKey: ['referral-link'],
    queryFn: async () => {
      try {
        const { data } = await api.get<ReferralInfo>('/users/referral');
        if (data?.link) return data;
      } catch {
        /* fallback ниже */
      }
      const origin = window.location.origin;
      return { link: user?.uid ? `${origin}/?ref=${user.uid}` : origin };
    },
    enabled: open,
  });

  if (!open) return null;

  const refLink = referral?.link ?? window.location.origin;
  const refCode = (() => {
    try { return new URL(refLink).searchParams.get('ref'); } catch { return null; }
  })();

  // Артефакт для шеринга (петля 3): публичная страница канала с брендом teleclaw,
  // если канал уже опубликован; иначе — обычная реф-ссылка.
  const shareArtifactUrl = channelHandle
    ? `${window.location.origin}/public/channel/${channelHandle}${refCode ? `?ref=${refCode}` : ''}`
    : refLink;

  const shareText = t('share.tgText', {
    subs: subscribers != null ? subscribers.toLocaleString() : '',
    handle: channelHandle ? `@${channelHandle}` : '',
  });

  const handleShareTelegram = (): void => {
    track('share', { surface, artifact_id: channelHandle });
    const url = `https://t.me/share/url?url=${encodeURIComponent(shareArtifactUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopyReferral = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(refLink);
      toast.success(t('share.copied'));
      track('invite_sent', { surface });
    } catch {
      toast.error(t('common.error'));
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-xl max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="absolute right-3 top-3 text-brand-muted hover:text-brand-ink"
          onClick={onClose}
          aria-label={t('common.cancel')}
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2 mb-2 text-brand-primary">
          <PartyPopper size={22} />
          <h3 className="text-xl font-bold">{t('share.title')}</h3>
        </div>
        <p className="text-brand-muted mb-5">{t('share.desc')}</p>

        <button type="button" className="btn-primary w-full flex items-center justify-center gap-2" onClick={handleShareTelegram}>
          <Share2 size={16} />
          {t('share.shareTelegram')}
        </button>

        <div className="mt-4 p-3 rounded-lg bg-brand-primary/5 border border-brand-primary/20">
          <div className="text-sm font-medium text-brand-ink mb-1">{t('share.referralHeading')}</div>
          <p className="text-xs text-brand-muted mb-2">{t('share.referralDesc')}</p>
          <div className="flex items-center gap-2">
            <input readOnly value={refLink} className="input flex-1 text-xs font-mono" onFocus={(e) => e.target.select()} />
            <button
              type="button"
              className="btn-secondary flex items-center gap-1 shrink-0"
              onClick={() => void handleCopyReferral()}
            >
              <Copy size={14} />
              {t('share.copy')}
            </button>
          </div>
        </div>

        <button type="button" className="w-full mt-5 text-sm text-brand-muted hover:text-brand-ink" onClick={onClose}>
          {t('share.later')}
        </button>
      </div>
    </div>
  );
}
